import { getByID } from '@adornis/baseql/operations/mongo';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration';
import { acss } from '@adornis/chemistry/directives/acss';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/stack/x-stack.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful';
import { useQuery } from '@adornis/functional-lit/use-query';
import { webcomponent } from '@adornis/functional-lit/webcomponent';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LASUser } from '../../../db/las-user';
import { UserAvatar } from '../../users/client/UserAvatar';
import { subscribeAllCommentReplies } from '../api/subscribeAllCommentReplies';
import { Comment } from '../db/Comment';
import { AddCommentDialog } from './AddCommentDialog';
import { EditCommentDialog } from './EditCommentDialog';

export const ViewCommentComponent = webcomponent(
  'view-comment',
  ({
    comment,
    globalSettingsClassName,
    onHover: highlightComment,
    isReply = false,
    hideActions,
  }: {
    comment: NarrowedEntity<Comment, { _id: 1; createdAt: 1; createdBy: 1; text: 1; updatedAt: 1 }>;
    globalSettingsClassName: string;
    onHover?: () => void;
    isReply?: boolean;
    hideActions?: boolean;
  }) => {
    return ViewComment({ comment, onHover: highlightComment, isReply, hideActions, globalSettingsClassName });
  },
);

export const ViewComment = stateful(
  ({
    comment,
    globalSettingsClassName,
    onHover: highlightComment,
    isReply = false,
    hideActions,
  }: {
    comment: NarrowedEntity<Comment, { _id: 1; createdAt: 1; createdBy: 1; text: 1; updatedAt: 1 }>;
    globalSettingsClassName: string;
    onHover?: () => void;
    isReply?: boolean;
    hideActions?: boolean;
  }) => {
    const { value: user, loading: isUserLoading } = useQuery(
      () => getByID(LASUser, comment.createdBy)({ _id: 1, zohoID: 1, email: 1 }),
      [comment.createdBy],
    );

    if (isUserLoading) return html` <x-loader></x-loader> `;
    if (!user) return nothing;

    const replies = useObservable(() => subscribeAllCommentReplies(comment._id)(Comment.allFields), [comment._id]);
    const [showReplies, setShowReplies] = useState(true);
    if (!replies) return nothing;

    const design = DesignSystem.currentTheme.getValue();

    const isOwnComment = user._id === comment.createdBy && true;

    return html`
      <x-grid
        columns=${`max-content 1fr`}
        space="md"
        ${acss({
          position: 'relative',
          paddingBottom: '20px',
        })}
      >
        ${UserAvatar.render({ user: user as LASUser })}
        <x-flex>
          <d-flex horizontal crossaxis-center space-between space="md" wrap>
            <d-text ${css({ position: 'relative' })}>
              <d-bold>
                ${user.email} • ${formatRelativeDate((comment.updatedAt ?? comment.createdAt).toJSDate())}
                ${comment.updatedAt
                  ? html` <d-text tiny ${css({ color: design.colors.accent })}> • (Bearbeitet) </d-text> `
                  : nothing}
              </d-bold>
            </d-text>
            ${highlightComment
              ? html`
                  <d-icon
                    ${css({ cursor: 'pointer' })}
                    solid
                    @click=${() => {
                      highlightComment?.();
                    }}
                  >
                    compass
                  </d-icon>
                `
              : nothing}
          </d-flex>
          <d-text> ${unsafeHTML(comment.text)} </d-text>
          <x-flex
            space="md"
            horizontal
            crossaxis-center
            ${css({ display: hideActions ? 'none !important' : '' })}
            ${acss({
              'd-text': {
                fontSize: '12px',
                cursor: 'pointer',
                color: design.colors.tone.secondaryText,
              },
            })}
          >
            <d-text
              @click=${() => {
                AddCommentDialog({ relatedEntityID: comment._id });
              }}
            >
              Antworten
            </d-text>
            ${isOwnComment
              ? html`
                  <d-text
                    @click=${async () => {
                      console.log('Edit now');

                      EditCommentDialog(comment as Comment, globalSettingsClassName);
                    }}
                  >
                    Bearbeiten
                  </d-text>
                  <d-text
                    ${acss({
                      '&:hover': {
                        color: design.colors.tone.emergency,
                      },
                    })}
                    @click=${() => comment.remove()}
                  >
                    Löschen
                  </d-text>
                `
              : nothing}
          </x-flex>
          ${replies.length
            ? html`
                <div ${css({ marginTop: '16px' })}>
                  ${showReplies
                    ? html`${repeat(
                        replies,
                        reply => reply._id,
                        reply => ViewComment({ comment: reply, isReply: true, globalSettingsClassName }),
                      )} `
                    : nothing}
                  <d-text
                    ${css({ textAlign: 'center', color: design.colors.tone.secondaryText, cursor: 'pointer' })}
                    @click=${() => setShowReplies(!showReplies)}
                  >
                    ${showReplies ? `Antworten ausblenden` : `${replies.length} Antworten einblenden`}
                  </d-text>
                </div>
              `
            : nothing}
        </x-flex>
      </x-grid>
    `;
  },
);

function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 30 && diffInSeconds >= 0) {
    return 'Gerade eben';
  }

  if (diffInSeconds < 0) {
    return 'In der Zukunft';
  }

  const intervals: [number, string][] = [
    [60, 'Sekunde'],
    [60, 'Minute'],
    [24, 'Stunde'],
    [7, 'Tage'],
    [4.35, 'Woche'],
    [12, 'Monat'],
    [Number.POSITIVE_INFINITY, 'Jahr'],
  ];

  let time = diffInSeconds;
  let unit = 'Sekunde';

  for (const [factor, name] of intervals) {
    if (time < factor) {
      const rounded = Math.floor(time);
      return `vor ${rounded} ${name}${rounded !== 1 ? 'n' : ''}`;
    }
    time /= factor;
    unit = name;
  }

  return 'Gerade eben';
}
